import React, { useCallback, useEffect, useState } from 'react';
import {
  buildProcurementInvoiceFilename,
  downloadProcurementInvoicePdf,
} from '../utils/downloadProcurementInvoicePdf';
import type { ProcurementInvoicePdfData } from '../utils/procurementInvoicePdf';
import { formatInvoiceBoxQty } from '../utils/procurementInvoicePdf';
import { formatPhoneKoreanInput } from '../utils/phone';
import { monthStartLocalDate, daysFromTodayLocalDate, toDateOnlyString } from '../utils/dateOnly';

const LINE_HEADERS = ['B/L', '품목', '수량', '포장', '중량(KG)', 'PLT'];

interface InvoiceSummary {
  id: string;
  shipment_date: string;
  destination_name: string;
  vehicle_number: string;
  line_count: number;
}

interface InvoiceLine {
  item_name: string;
  box_qty: string | number | null;
  pack_unit?: string | null;
  weight_kg: number | null;
  pallet_qty: number | null;
  line_memo: string | null;
}

interface LoadedInvoice {
  id: string;
  shipment_date: string;
  destination_name: string;
  destination_address: string;
  destination_phone_landline: string;
  destination_phone_mobile: string;
  destination_phones: string;
  destination_manager_name: string;
  release_location: string;
  vehicle_number: string;
  driver_contact: string;
  arrival_at: string | null;
  memo: string;
  lines: InvoiceLine[];
}

const ProcurementInvoiceViewerPage: React.FC = () => {
  const [invoiceList, setInvoiceList] = useState<InvoiceSummary[]>([]);
  const [invoice, setInvoice] = useState<LoadedInvoice | null>(null);
  const [listStart, setListStart] = useState(() => monthStartLocalDate());
  const [listEnd, setListEnd] = useState(() => daysFromTodayLocalDate(7));
  const [loading, setLoading] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const loadInvoiceList = useCallback(async () => {
    const params = new URLSearchParams({ start: listStart, end: listEnd });
    const res = await fetch(`/api/procurement/customer/invoices?${params}`);
    if (res.ok) setInvoiceList(await res.json());
  }, [listStart, listEnd]);

  useEffect(() => { loadInvoiceList(); }, [loadInvoiceList]);

  const loadInvoice = async (id: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/procurement/customer/invoices/${id}`);
      if (!res.ok) throw new Error('송장을 불러오지 못했습니다.');
      const inv = await res.json();
      setInvoice({
        id: inv.id,
        shipment_date: toDateOnlyString(inv.shipment_date),
        destination_name: inv.destination_name || '',
        destination_address: inv.destination_address || '',
        destination_phone_landline: inv.destination_phone_landline || '',
        destination_phone_mobile: inv.destination_phone_mobile || '',
        destination_phones: inv.destination_phones || '',
        destination_manager_name: inv.destination_manager_name || '',
        release_location: inv.release_location || '',
        vehicle_number: inv.vehicle_number || '',
        driver_contact: inv.driver_contact || '',
        arrival_at: inv.arrival_at ? toDateOnlyString(inv.arrival_at) : null,
        memo: inv.memo || '',
        lines: inv.lines || [],
      });
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '조회 실패');
    } finally {
      setLoading(false);
    }
  };

  const buildPdfData = (): ProcurementInvoicePdfData | null => {
    if (!invoice) return null;
    return {
      shipmentDate: invoice.shipment_date,
      destinationName: invoice.destination_name,
      destinationAddress: invoice.destination_address,
      destinationPhoneLandline: invoice.destination_phone_landline,
      destinationPhoneMobile: invoice.destination_phone_mobile,
      destinationPhones: invoice.destination_phones,
      destinationManager: invoice.destination_manager_name,
      releaseLocation: invoice.release_location,
      vehicleNumber: invoice.vehicle_number,
      driverContact: formatPhoneKoreanInput(invoice.driver_contact),
      arrivalAt: invoice.arrival_at,
      memo: invoice.memo,
      lines: invoice.lines.map(l => ({
        lineMemo: l.line_memo,
        itemName: l.item_name,
        boxQty: l.box_qty,
        packUnit: l.pack_unit,
        weightKg: l.weight_kg,
        palletQty: l.pallet_qty,
        releaseLocation: '',
      })),
    };
  };

  const handlePdfDownload = async () => {
    const pdfData = buildPdfData();
    if (!pdfData) return;
    setPdfGenerating(true);
    try {
      const filename = buildProcurementInvoiceFilename(
        pdfData.shipmentDate,
        pdfData.destinationName,
        pdfData.vehicleNumber
      );
      await downloadProcurementInvoicePdf(pdfData, filename);
    } catch (e) {
      console.error('송장 PDF 생성 오류:', e);
      alert('송장 PDF 생성 중 오류가 발생했습니다.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const fieldStyle: React.CSSProperties = {
    padding: '6px 8px',
    borderRadius: 4,
    border: '1px solid #e2e8f0',
    fontSize: 14,
    background: '#f8fafc',
    minHeight: 34,
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 13,
    fontWeight: 600,
  };

  return (
    <div style={{ height: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', height: '100%' }}>
        <div style={{ flex: '1 1 520px', minWidth: 320 }}>
          {!invoice && !loading ? (
            <div style={{ padding: 48, color: '#888', textAlign: 'center', border: '1px dashed #ccc', borderRadius: 8 }}>
              송장 목록에서 송장을 선택하세요.
            </div>
          ) : loading ? (
            <div style={{ padding: 24, color: '#888' }}>불러오는 중…</div>
          ) : invoice ? (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 12,
                marginBottom: 16,
                padding: 16,
                background: '#f8fafc',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
              }}>
                <label style={labelStyle}>
                  출고일
                  <div style={fieldStyle}>{invoice.shipment_date || '—'}</div>
                </label>
                <label style={labelStyle}>
                  입고처(공장)
                  <div style={fieldStyle}>{invoice.destination_name || '—'}</div>
                </label>
                <label style={labelStyle}>
                  출고장소
                  <div style={fieldStyle}>{invoice.release_location || '—'}</div>
                </label>
                <label style={labelStyle}>
                  차량번호
                  <div style={fieldStyle}>{invoice.vehicle_number || '—'}</div>
                </label>
                <label style={labelStyle}>
                  기사 연락처
                  <div style={fieldStyle}>{formatPhoneKoreanInput(invoice.driver_contact) || '—'}</div>
                </label>
                <label style={labelStyle}>
                  도착일
                  <div style={fieldStyle}>{invoice.arrival_at || '—'}</div>
                </label>
                <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
                  비고 (냉동 온도 등)
                  <div style={fieldStyle}>{invoice.memo || '—'}</div>
                </label>
              </div>

              <div style={{ overflow: 'auto', border: '1px solid #ddd', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: '#f5f7fa' }}>
                      <th style={{ border: '1px solid #ddd', padding: 8, width: 48 }}>#</th>
                      {LINE_HEADERS.map(h => (
                        <th key={h} style={{ border: '1px solid #ddd', padding: 8 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.lines.length === 0 ? (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#888' }}>품목 없음</td></tr>
                    ) : (
                      invoice.lines.map((line, idx) => (
                        <tr key={idx}>
                          <td style={{ border: '1px solid #eee', padding: 8, textAlign: 'center' }}>{idx + 1}</td>
                          <td style={{ border: '1px solid #eee', padding: 8 }}>{line.line_memo || ''}</td>
                          <td style={{ border: '1px solid #eee', padding: 8 }}>{line.item_name}</td>
                          <td style={{ border: '1px solid #eee', padding: 8, textAlign: 'right' }}>{formatInvoiceBoxQty(line.box_qty) || ''}</td>
                          <td style={{ border: '1px solid #eee', padding: 8, textAlign: 'center' }}>{line.pack_unit || ''}</td>
                          <td style={{ border: '1px solid #eee', padding: 8, textAlign: 'right' }}>{line.weight_kg ?? ''}</td>
                          <td style={{ border: '1px solid #eee', padding: 8, textAlign: 'right' }}>{line.pallet_qty ?? ''}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  onClick={handlePdfDownload}
                  disabled={pdfGenerating}
                  style={{
                    padding: '8px 14px',
                    background: '#1565c0',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    cursor: pdfGenerating ? 'wait' : 'pointer',
                    fontWeight: 600,
                  }}
                >
                  {pdfGenerating ? 'PDF 생성 중…' : '송장 PDF'}
                </button>
              </div>
            </>
          ) : null}
        </div>

        <div style={{ flex: '0 0 300px', maxWidth: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, background: '#fff' }}>
          <div style={{ fontWeight: 700, marginBottom: 8, color: '#334155' }}>송장 목록</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            <input type="date" value={listStart} onChange={e => setListStart(e.target.value)} style={{ ...fieldStyle, flex: 1, minWidth: 120, background: '#fff' }} />
            <input type="date" value={listEnd} onChange={e => setListEnd(e.target.value)} style={{ ...fieldStyle, flex: 1, minWidth: 120, background: '#fff' }} />
            <button type="button" onClick={loadInvoiceList} style={{ padding: '6px 10px', fontSize: 13, cursor: 'pointer' }}>조회</button>
          </div>
          <div style={{ maxHeight: 480, overflow: 'auto' }}>
            {invoiceList.length === 0 ? (
              <div style={{ color: '#888', fontSize: 13, padding: 8 }}>기간 내 송장 없음</div>
            ) : (
              invoiceList.map(inv => (
                <button
                  key={inv.id}
                  type="button"
                  onClick={() => loadInvoice(inv.id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    marginBottom: 6,
                    border: invoice?.id === inv.id ? '2px solid #1976d2' : '1px solid #e2e8f0',
                    borderRadius: 6,
                    background: invoice?.id === inv.id ? '#e3f2fd' : '#fafafa',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{toDateOnlyString(inv.shipment_date)}</div>
                  <div>{inv.destination_name} · {inv.line_count}품목</div>
                  {inv.vehicle_number && <div style={{ color: '#64748b' }}>{inv.vehicle_number}</div>}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcurementInvoiceViewerPage;
